import React from 'react';
import { Loader } from 'src/components';
import { FetchError } from 'src/types/TApiQueries';
import { DataAsOf } from 'src/types/TDeliveryAnalytics';
import styles from '../styles.module.scss';
import { planUpgradeRequired } from 'src/api/utils/errorHandlers';
import { PlanUpgradeBanner } from 'src/components/FeatureUnavailable/PlanUpgradeBanner';

/** Card section with a title row that can host right-aligned controls
 *  (metric/dimension toggles, the data-as-of badge) plus an optional
 *  description. The shared AdminSettingsSection only takes a title, so we
 *  use this richer local variant across the analytics tabs. */
export const Section: React.FC<{
  title: string;
  description?: string;
  headerRight?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, description, headerRight, children }) => (
  <section className={styles.section}>
    <div className={styles.sectionHeader}>
      <div>
        <h2 className={styles.sectionTitle}>{title}</h2>
        {description && <p className={styles.sectionDesc}>{description}</p>}
      </div>
      {headerRight && <div className={styles.sectionHeaderRight}>{headerRight}</div>}
    </div>
    <div className={styles.sectionBody}>{children}</div>
  </section>
);

export function formatDataAsOf(value: DataAsOf): string {
  if (!value) return 'No data in range yet';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return `Data as of ${d.toLocaleString(undefined, {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

export const DataAsOfBadge: React.FC<{ value?: DataAsOf; loading?: boolean }> = ({
  value,
  loading,
}) => (
  <span className={styles.dataAsOf}>
    {loading ? 'Loading…' : formatDataAsOf(value ?? null)}
  </span>
);

function errorMessage(error: FetchError | null | undefined): string {
  const e = error as { status?: number; message?: string } | null | undefined;
  if (!e) return 'Something went wrong loading analytics.';
  if (e.status === 403) return 'You do not have permission to view delivery analytics.';
  if (e.status === 502 || e.status === 503)
    return 'The analytics service is temporarily unavailable. Try again shortly.';
  return e.message || 'Failed to load analytics data.';
}

/**
 * Wraps a query panel: shows a loader, a friendly error banner, an empty-state
 * message, or the children. Keeps every tab's state handling consistent.
 */
export const AsyncSection: React.FC<{
  isLoading: boolean;
  error?: FetchError | null;
  isEmpty?: boolean;
  emptyLabel?: string;
  children: React.ReactNode;
}> = ({ isLoading, error, isEmpty, emptyLabel, children }) => {
  if (isLoading) {
    return (
      <div className={styles.sectionLoading}>
        <Loader />
      </div>
    );
  }
  if (error) {
    const gate = planUpgradeRequired(error);
    if (gate) {
      return <PlanUpgradeBanner gate={gate} />;
    }
    return <div className={styles.errorBanner}>{errorMessage(error)}</div>;
  }
  if (isEmpty) {
    return (
      <div className={styles.emptyState}>
        {emptyLabel || 'No data for the selected filters and time range.'}
      </div>
    );
  }
  return <>{children}</>;
};
